// frontend/src/pages/BookingDetail.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { bookings as bookingsApi, payments as paymentsApi, staff as staffApi, notifs as notifsApi, rooms as roomsApi, giftCards as giftCardsApi, receipts as receiptsApi, settings as settingsApi, refundRequests as refundRequestsApi, pricingPeriods as pricingPeriodsApi, partnersApi } from '../utils/api';
import MoveBookingModal from '../components/MoveBookingModal';
import { useSocketEvent } from '../utils/socket';
import { useAuth } from '../utils/auth';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, UserCheck, CreditCard, Bell, CheckCircle2, Edit2, Save, X, Clock, LogIn, Gift, FileText, Mail, Send, Eye, Percent, Banknote, CreditCard as CardIcon, ScrollText, Landmark, Phone, Delete } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_LABELS = { confirmed:'Confirmé', pending:'En attente', cancelled:'Annulé', completed:'Terminé', no_show:'No-show' };
const STATUS_BADGE  = { confirmed:'badge-green', pending:'badge-amber', cancelled:'badge-red', completed:'badge-purple', no_show:'badge-gray' };
const PAY_BADGE     = { paid:'badge-green', partial:'badge-amber', unpaid:'badge-red' };
const PAY_LABELS    = { paid:'Payé', partial:'Acompte versé', unpaid:'Impayé' };

// ── Helpers ───────────────────────────────────────────────────
function formatPhone(val) {
  const digits = val.replace(/\D/g,'').slice(0,10);
  return digits.replace(/(\d{2})(?=\d)/g,'$1 ').trim();
}

const CHILD_KEYWORDS = ['enfant','child','junior'];
function calcPricing(profiles, qtys, appliedType) {
  const totalPlayers = Object.values(qtys).reduce((s,q)=>s+q,0);
  const type = appliedType || 'hc';
  let amount = 0;
  const breakdown = [];
  for (const p of profiles) {
    const qty = qtys[p.price_profile_id]||0;
    if (!qty) continue;
    const tiers = (p.tiers||[]).filter(t=>Math.max(1,totalPlayers)>=(t.min_players||1));
    const tier = tiers.length>0?tiers[tiers.length-1]:(p.tiers||[])[0];
    const priceFlat = type === 'hp' ? tier?.priceFlatHp     : tier?.priceFlatHc;
    const pricePerP = type === 'hp' ? tier?.pricePerPersonHp : tier?.pricePerPersonHc;
    const lt = priceFlat ? parseFloat(priceFlat) : (parseFloat(pricePerP||0)*qty);
    amount+=lt;
    breakdown.push({ profileId: p.price_profile_id, profileName: p.profile_name, qty, lineTotal: lt });
  }
  return { total: amount.toFixed(2), totalPlayers, breakdown };
}

function AdminPlayerPicker({ prices, initialPlayerCount, initialBreakdown, onChange }) {
  const profiles = (prices||[]).filter(Boolean);
  const [qtys, setQtys] = useState({});
  useEffect(()=>{
    if (profiles.length>0) {
      const q={};
      for (const p of profiles) q[p.price_profile_id]=0;
      // Reconstruire depuis le breakdown existant si disponible
      const bd = Array.isArray(initialBreakdown) ? initialBreakdown : null;
      if (bd?.length) {
        for (const line of bd) {
          if (q[line.profileId] !== undefined) q[line.profileId] = line.qty||0;
        }
      } else {
        // Fallback : mettre le premier profil non-enfant à initialPlayerCount
        const mainProfile = profiles.find(p=>!['enfant','child','junior'].some(k=>p.profile_name?.toLowerCase().includes(k)));
        if (mainProfile && initialPlayerCount>0) q[mainProfile.price_profile_id]=initialPlayerCount;
      }
      setQtys(q);
      onChange(calcPricing(profiles,q));
    }
  // eslint-disable-next-line
  },[profiles.map(p=>p.price_profile_id).join(',')]);

  const upd=(id,delta)=>{
    setQtys(prev=>{
      const next={...prev,[id]:Math.max(0,(prev[id]||0)+delta)};
      onChange(calcPricing(profiles,next));
      return next;
    });
  };
  const total=Object.values(qtys).reduce((s,q)=>s+q,0);
  return (
    <div style={{display:'flex',flexDirection:'column',gap:6}}>
      {profiles.map(p=>{
        const qty=qtys[p.price_profile_id]||0;
        const tiers=(p.tiers||[]).filter(t=>Math.max(1,total)>=(t.min_players||1));
        const tier=tiers.length>0?tiers[tiers.length-1]:(p.tiers||[])[0];
        const priceFlat=tier?.priceFlatHc||tier?.priceFlatHp;
        const pricePerP=tier?.pricePerPersonHc||tier?.pricePerPersonHp;
        const unit=priceFlat?null:parseFloat(pricePerP||0);
        const lt=priceFlat?parseFloat(priceFlat):unit*qty;
        return (
          <div key={p.price_profile_id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderRadius:8,
            background:qty>0?'rgba(124,92,252,0.08)':'var(--bg-elevated)',
            border:`1px solid ${qty>0?'rgba(124,92,252,0.2)':'var(--border)'}`}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600}}>{p.profile_name}</div>
              <div style={{fontSize:11,color:'var(--text-3)'}}>{priceFlat?`${parseFloat(priceFlat).toFixed(0)} € fixe`:unit?`${unit.toFixed(0)} €/pers`:'—'}</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <button type="button" onClick={()=>upd(p.price_profile_id,-1)} disabled={qty<=0}
                style={{width:28,height:28,borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-base)',
                  color:qty>0?'var(--text-1)':'var(--text-3)',cursor:qty>0?'pointer':'not-allowed',fontSize:16,
                  display:'flex',alignItems:'center',justifyContent:'center'}}>−</button>
              <input type="number" min={0} value={qty}
                onChange={e=>{const v=Math.max(0,parseInt(e.target.value)||0);setQtys(prev=>{const n={...prev,[p.price_profile_id]:v};onChange(calcPricing(profiles,n));return n;});}}
                style={{width:36,textAlign:'center',background:'var(--bg-base)',border:'1px solid var(--border)',
                  borderRadius:6,color:'var(--text-1)',fontSize:14,fontWeight:700,padding:'3px 0',fontFamily:'monospace'}}/>
              <button type="button" onClick={()=>upd(p.price_profile_id,1)}
                style={{width:28,height:28,borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-base)',
                  color:'var(--text-1)',cursor:'pointer',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
            </div>
            <span style={{minWidth:50,textAlign:'right',fontWeight:700,fontSize:13,color:qty>0?'var(--teal)':'var(--text-3)'}}>
              {qty>0?`${lt.toFixed(0)} €`:'—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Modale rappel avec preview ───────────────────────────────
function ReminderModal({ bookingId, onClose, onSent }) {
  const [templates,  setTemplates]  = useState([]);
  const [selected,   setSelected]   = useState('booking_reminder');
  const [preview,    setPreview]    = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [sending,    setSending]    = useState(false);

  useEffect(() => {
    notifsApi.templates().then(r => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    notifsApi.preview(bookingId, { templateCode: selected })
      .then(r => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setLoading(false));
  }, [selected, bookingId]);

  const send = async () => {
    setSending(true);
    try {
      await notifsApi.send(bookingId, { templateCode: selected });
      onSent();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Erreur envoi');
    } finally { setSending(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:540 }}>
        <h3 className="modal-title">Envoyer une notification</h3>

        {/* Choix du template */}
        <div className="form-group">
          <label className="form-label">Type de message</label>
          <select className="input" value={selected} onChange={e => setSelected(e.target.value)}>
            {templates.filter(t => t.is_active).map(t => (
              <option key={t.id} value={t.code}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Preview */}
        {loading ? (
          <div style={{ textAlign:'center', padding:20 }}><span className="spinner"/></div>
        ) : preview ? (
          <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:14, marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <Eye size={13} color="var(--text-3)"/>
              <span style={{ fontSize:12, fontWeight:700, color:'var(--text-2)' }}>Aperçu</span>
              <span style={{ marginLeft:'auto', fontSize:12, color:'var(--text-3)' }}>
                → {preview.recipient || "Pas d'email"}
              </span>
            </div>
            <div style={{ fontSize:12, fontWeight:600, marginBottom:8, color:'var(--text-1)' }}>
              {preview.subject}
            </div>
            <pre style={{ fontSize:11, color:'var(--text-2)', margin:0, whiteSpace:'pre-wrap',
              fontFamily:'monospace', lineHeight:1.6, maxHeight:200, overflowY:'auto' }}>
              {preview.body}
            </pre>
          </div>
        ) : (
          <div style={{ padding:'12px 14px', borderRadius:8, marginBottom:16,
            background:'rgba(255,77,109,0.06)', border:'1px solid var(--red)', fontSize:12, color:'var(--red)' }}>
            Impossible de charger l'aperçu
          </div>
        )}

        {!preview?.recipient && (
          <div style={{ padding:'8px 12px', borderRadius:8, marginBottom:12,
            background:'rgba(245,166,35,0.08)', border:'1px solid var(--amber)',
            fontSize:12, color:'var(--amber)' }}>
            ⚠ Ce client n'a pas d'adresse email — l'envoi échouera
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={send}
            disabled={sending || !preview?.recipient}>
            {sending
              ? <span className="spinner" style={{width:14,height:14}}/>
              : <><Send size={13}/> Envoyer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Fidélité : joueurs de la réservation + progression individuelle ─────
function LoyaltyPlayersCard({ bookingId, booking }) {
  const [players, setPlayers] = useState(null);

  useEffect(() => {
    if (!bookingId) return;
    bookingsApi.players(bookingId).then(r => setPlayers(r.data.players || [])).catch(() => setPlayers([]));
  }, [bookingId]);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
          <Gift size={15}/> Fidélité
        </span>
      </div>

      {booking?.loyalty_reward_applied && (
        <div style={{ marginBottom:12, padding:'8px 12px', borderRadius:8,
          background:'rgba(0,210,180,0.08)', border:'1px solid var(--teal)', fontSize:12, color:'var(--teal)' }}>
          🎁 Récompense appliquée : -{parseFloat(booking.loyalty_discount_amount || 0).toFixed(2)} € sur cette réservation
        </div>
      )}

      {players === null ? (
        <div style={{ textAlign:'center', padding:16 }}><span className="spinner" style={{width:14,height:14}}/></div>
      ) : players.length === 0 ? (
        <p className="text-xs text-muted">Aucun joueur enregistré</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {players.map(p => (
            <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'8px 10px', background:'var(--bg-elevated)', borderRadius:8 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13 }}>
                  {p.first_name} {p.last_name || ''} {p.is_contact && <span className="text-xs text-muted">(contact)</span>}
                </div>
                {!p.customer_id && (
                  <div className="text-xs text-muted">Non suivi (pas de fiche client)</div>
                )}
              </div>
              {p.customer_id && (
                <div style={{ textAlign:'right', fontSize:11, color:'var(--text-3)' }}>
                  {p.sessions_count ?? 0} session(s)
                  {p.rewards_available > 0 && (
                    <div style={{ color:'var(--teal)', fontWeight:700 }}>{p.rewards_available} récompense(s) dispo</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Remise manuelle / partenaire, activable depuis la réservation par
// n'importe quel membre du staff ayant accès (gating serveur : rôle 'staff'
// minimum). Une seule remise active à la fois sur la réservation.
function DiscountCard({ bookingId, booking, canEdit, onChanged, locked }) {
  const [partners, setPartners] = useState(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('manual'); // 'manual' | 'partner'
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [reason, setReason] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    partnersApi.list().then(r => setPartners((r.data.partners || []).filter(p => p.active))).catch(() => setPartners([]));
  }, []);

  const applied = !!booking?.manual_discount_amount;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = mode === 'partner'
        ? { type: 'partner', partnerId }
        : { type: 'manual', discountType, discountValue: parseFloat(discountValue) || 0, reason: reason || null };
      await bookingsApi.applyDiscount(bookingId, payload);
      toast.success('Remise appliquée');
      setOpen(false);
      setDiscountValue(''); setReason(''); setPartnerId('');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Retirer la remise de cette réservation ?')) return;
    try {
      await bookingsApi.removeDiscount(bookingId);
      toast.success('Remise retirée');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  // Version compacte : pas de card-header dédié, s'intègre directement dans
  // le bloc Paiements (à la demande : 5 blocs sur la fiche résa, c'était
  // trop — la remise n'a pas besoin de sa propre carte pleine largeur).
  return (
    <div style={{ marginBottom:12 }}>
      {!applied && !open && canEdit && !locked && (
        <button type="button" onClick={() => setOpen(true)}
          style={{
            display:'flex', alignItems:'center', gap:8, width:'100%',
            padding:'10px 14px', borderRadius:10, cursor:'pointer',
            background:'rgba(245,166,35,0.08)', border:'1px dashed var(--amber)',
            color:'var(--amber)', fontSize:13, fontWeight:600,
          }}>
          <Percent size={15}/> Appliquer une remise manuelle ou partenaire
        </button>
      )}

      {applied ? (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'8px 12px', borderRadius:8, background:'rgba(245,166,35,0.08)',
          border:'1px solid var(--amber)', fontSize:12, color:'var(--amber)' }}>
          <span>{booking.manual_discount_label} : -{parseFloat(booking.manual_discount_amount).toFixed(2)} €</span>
          {canEdit && (locked
            ? <span style={{ fontSize:11, color:'var(--text-3)' }}>🔒 verrouillée (paiement en caisse enregistré)</span>
            : <button className="btn btn-ghost btn-sm" onClick={remove}><X size={13}/> Retirer</button>)}
        </div>
      ) : open ? (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ display:'flex', gap:8 }}>
            <button type="button" className={`btn btn-sm ${mode==='manual' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setMode('manual')}>Remise libre</button>
            <button type="button" className={`btn btn-sm ${mode==='partner' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setMode('partner')}>Partenaire</button>
          </div>

          {mode === 'manual' ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="input" value={discountType} onChange={e => setDiscountType(e.target.value)}>
                    <option value="percent">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (€)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{discountType === 'percent' ? 'Réduction (%)' : 'Réduction (€)'}</label>
                  <input type="number" step="0.01" min="0" className="input" value={discountValue}
                    onChange={e => setDiscountValue(e.target.value)} placeholder="0" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Motif (optionnel)</label>
                <input className="input" value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="Ex : geste commercial suite à un imprévu" />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label className="form-label">Partenaire</label>
              {partners === null ? (
                <div style={{ padding:8 }}><span className="spinner" style={{width:14,height:14}}/></div>
              ) : partners.length === 0 ? (
                <p className="text-xs text-muted">Aucun partenaire actif — à créer dans Admin &gt; Partenaires</p>
              ) : (
                <select className="input" value={partnerId} onChange={e => setPartnerId(e.target.value)}>
                  <option value="">— Choisir —</option>
                  {partners.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} (-{p.discount_value}{p.discount_type === 'percent' ? '%' : '€'})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Annuler</button>
            <button className="btn btn-primary btn-sm" onClick={submit}
              disabled={saving || (mode === 'partner' && !partnerId) || (mode === 'manual' && !discountValue)}>
              {saving ? <span className="spinner" style={{width:14,height:14}}/> : 'Appliquer'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Pavé numérique tactile pour la saisie d'un montant (encaissement) —
// saisie "caisse" par décalage de chiffres (comme sur un TPE/caisse
// enregistreuse) : chaque chiffre tapé pousse les précédents vers la
// gauche et les deux derniers deviennent les centimes (1 → 0,01 ; 2 →
// 0,12 ; 5 → 1,25 ; 0 → 12,50...). Pas de bouton virgule à gérer, et pas
// besoin de viser une position dans le champ — plus rapide au doigt.
const centsOf = (v) => v ? Math.round(parseFloat(v) * 100) : 0;
const fromCents = (c) => c === 0 ? '' : (c / 100).toFixed(2);

// Logique de décalage partagée entre le pavé tactile (clic sur un bouton)
// et le clavier physique (touche pressée) : chaque chiffre pousse les
// précédents vers la gauche, les deux derniers = centimes. '⌫' fait
// remonter d'un cran (retire le dernier chiffre), '00' ajoute deux zéros.
function applyNumpadKey(value, key) {
  if (key === '⌫') return fromCents(Math.floor(centsOf(value) / 10));
  if (key === '00') return fromCents(centsOf(value) * 100);
  if (/^[0-9]$/.test(key)) return fromCents(centsOf(value) * 10 + parseInt(key, 10));
  return value;
}

function NumPad({ value, onChange }) {
  const KEYS = ['7','8','9','4','5','6','1','2','3','00','0','⌫'];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8, marginBottom:12, maxWidth:340 }}>
      {KEYS.map(k => (
        <button type="button" key={k} onClick={() => onChange(applyNumpadKey(value, k))}
          style={{
            padding:'16px 0', borderRadius:10, fontSize:19, fontWeight:700,
            background:'var(--bg-base)', color:'var(--text-1)',
            border:'1px solid var(--border)', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap:4,
          }}>
          {k === '⌫' ? <Delete size={18}/> : k}
        </button>
      ))}
    </div>
  );
}

export default function BookingDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { can }   = useAuth();
  if (!can('bookings.view')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [booking,   setBooking]   = useState(null);
  const [methods,   setMethods]   = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [payAmount, setPayAmount] = useState('');
  const [payMethodId, setPayMethodId] = useState('');
  const [splitParts, setSplitParts] = useState('');
  const [splitMode, setSplitMode] = useState('equal'); // 'equal' | 'tariff'
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [giftCode, setGiftCode]     = useState('');
  const [genReceipt, setGenReceipt] = useState(false);
  const [moveModal, setMoveModal]   = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [tva, setTva]               = useState({ rate:0, mode:'ht' });
  const [giftCard, setGiftCard]     = useState(null);
  const [checkingGift, setCheckGift] = useState(false);
  const [editingContact,  setEditingContact]  = useState(false);
  const [editingPlayers, setEditingPlayers] = useState(false);
  const [editForm,  setEditForm]  = useState({});
  const [roomPrices,   setRoomPrices]   = useState([]);
  const [appliedType,  setAppliedType]  = useState('hc');
  const [remindModal, setRemindModal] = useState(false);
  const [bookingNotifs, setBookingNotifs] = useState([]);
  const [pendingRefund, setPendingRefund] = useState(null);
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  // Une demande de remboursement "trop perçu" déjà en attente pour CETTE
  // réservation ne doit pas pouvoir en créer une deuxième — on vérifie donc
  // les demandes en attente à chaque chargement de page.
  useEffect(() => {
    refundRequestsApi.list('pending')
      .then(r => setPendingRefund((r.data.refundRequests || []).find(rr => rr.booking_id === id) || null))
      .catch(() => {});
  }, [id]);

  const load = useCallback(async () => {
    try {
      const r = await bookingsApi.get(id);
      const b = r.data.booking;
      setBooking(b);
      const pb = b.price_breakdown
        ? (Array.isArray(b.price_breakdown)
            ? b.price_breakdown
            : (() => { try { return JSON.parse(b.price_breakdown); } catch { return null; } })())
        : null;
      setEditForm({
        customerName:   b.customer_name,
        customerEmail:  b.customer_email,
        customerPhone:  b.customer_phone   || '',
        internalNotes:  b.internal_notes   || '',
        playerCount:    b.player_count,
        totalAmount:    b.total_amount,
        priceBreakdown: pb,
        customerType:   b.customer_type    || 'individual',
        companyName:    b.company_name     || '',
        companySiret:   b.company_siret    || '',
        companyVat:     b.company_vat      || '',
        companyAddress: b.company_address  || '',
      });
      roomsApi.get(b.room_id).then(r => {
        const ps = (r.data.room?.prices||[]).filter(Boolean);
        ps.sort((a, b) => {
          const priceA = (a.tiers?.[0]?.priceFlatHp||a.tiers?.[0]?.priceFlatHc) ?? ((a.tiers?.[0]?.pricePerPersonHp||a.tiers?.[0]?.pricePerPersonHc)*2) ?? 0;
          const priceB = (b.tiers?.[0]?.priceFlatHp||b.tiers?.[0]?.priceFlatHc) ?? ((b.tiers?.[0]?.pricePerPersonHp||b.tiers?.[0]?.pricePerPersonHc)*2) ?? 0;
          return priceB - priceA;
        });
        setRoomPrices(ps);
      }).catch(()=>{});

      // Résoudre le type tarifaire HC/HP selon le créneau
      // session_start peut être string 'HH:MM:SS', 'HH:MM', ou Date
      let sessionTime = null;
      if (b.session_start) {
        if (typeof b.session_start === 'string') {
          sessionTime = b.session_start.slice(0,5);
        } else if (b.session_start instanceof Date) {
          sessionTime = b.session_start.toTimeString().slice(0,5);
        } else {
          sessionTime = String(b.session_start).slice(0,5);
        }
      }
      console.log('[BookingDetail] resolve with', { roomId: b.room_id, date: b.session_date?.slice(0,10), time: sessionTime });
      if (b.room_id && b.session_date && sessionTime && sessionTime.length >= 4) {
        pricingPeriodsApi.resolve({
          roomId: b.room_id,
          date:   b.session_date.slice(0,10),
          time:   sessionTime,
        }).then(r => {
          console.log('[BookingDetail] appliedType:', r.data.appliedType);
          setAppliedType(r.data.appliedType || null);
        }).catch(e => {
          console.error('[BookingDetail] resolve error:', e.message);
          setAppliedType(null);
        });
      }
      // Charger les notifications de cette résa
      notifsApi.list({ bookingId: id }).then(r => setBookingNotifs(r.data.notifications || [])).catch(() => {});

      const [m, s] = await Promise.all([
        paymentsApi.methods(),
        staffApi.available((() => {
          const d = new Date(b.session_date);
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        })(), b.room_id, b.session_start?.slice(0,5)),
      ]);
      setMethods(m.data.methods);
      if (m.data.methods[0]) setPayMethodId(m.data.methods[0].id);
      setStaffList(s.data.staff);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement de la réservation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Charger la TVA
  useEffect(() => {
    settingsApi.get().then(r => {
      const arr = r.data.settings || [];
      const get = (key) => (Array.isArray(arr) ? arr.find(s=>s.key===key)?.value : arr[key]) || '';
      setTva({ rate: parseFloat(get('tva_rate')||0), mode: get('tva_mode')||'ht' });
    }).catch(()=>{});
  }, []);

  useSocketEvent('booking:updated', d => { if (d.booking?.id === id) load(); });
  useSocketEvent('payment:added',   d => { if (d.bookingId === id) load(); });

  const saveContact = async () => {
    try {
      await bookingsApi.update(id, {
        customerName:   editForm.customerName,
        customerEmail:  editForm.customerEmail,
        customerPhone:  editForm.customerPhone,
        internalNotes:  editForm.internalNotes,
        customerType:   editForm.customerType,
        companyName:    editForm.customerType === 'company' ? editForm.companyName    : null,
        companySiret:   editForm.customerType === 'company' ? editForm.companySiret   : null,
        companyVat:     editForm.customerType === 'company' ? editForm.companyVat     : null,
        companyAddress: editForm.customerType === 'company' ? editForm.companyAddress : null,
      });
      toast.success('Coordonnées mises à jour');
      setEditingContact(false);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const savePlayers = async () => {
    try {
      await bookingsApi.update(id, {
        playerCount:    editForm.playerCount,
        totalAmount:    editForm.totalAmount,
        priceBreakdown: editForm.priceBreakdown ? JSON.stringify(editForm.priceBreakdown) : undefined,
      });
      toast.success('Composition mise à jour');
      setEditingPlayers(false);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const handleReceipt = async (action) => {
    setGenReceipt(true);
    try {
      const r = await receiptsApi.generate(id);
      if (action === 'download') {
        const dl = await receiptsApi.download(id);
        const url = URL.createObjectURL(new Blob([dl.data], {type:'application/pdf'}));
        const a = document.createElement('a');
        a.href = url; a.download = `${booking.booking_ref}.pdf`; a.click();
        URL.revokeObjectURL(url);
      } else if (action === 'send') {
        await receiptsApi.send(id);
        toast.success('Reçu envoyé par email');
      }
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setGenReceipt(false); }
  };

  const checkGiftCard = async () => {
    if (!giftCode.trim()) return;
    setCheckGift(true);
    try {
      const r = await giftCardsApi.check(giftCode.trim());
      setGiftCard(r.data.giftCard);
      toast.success(`Bon valide — ${parseFloat(r.data.giftCard.remaining_amount).toFixed(2)} € disponible`);
    } catch(e) { toast.error(e.response?.data?.error || 'Code invalide'); setGiftCard(null); }
    finally { setCheckGift(false); }
  };

  const applyGiftCard = async () => {
    if (!giftCard) return;
    // Utiliser au maximum le reste dû, sinon tout le solde disponible du bon
    const maxUsable = Math.min(
      parseFloat(giftCard.remaining_amount),
      remaining > 0 ? remaining : parseFloat(giftCard.remaining_amount)
    );
    if (maxUsable <= 0) return toast.error('Aucun montant à déduire');
    try {
      const r = await giftCardsApi.apply({ code: giftCode, bookingId: id, amount: maxUsable });
      toast.success(`${parseFloat(r.data.amountUsed).toFixed(2)} € déduits — solde bon : ${parseFloat(r.data.remaining).toFixed(2)} €`);
      setGiftCard(null); setGiftCode('');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  // methodId en paramètre (plutôt que de relire l'état payMethodId) : cliquer
  // sur une icône de moyen de paiement enregistre le paiement immédiatement,
  // sans passer par un second clic sur "Enregistrer" — setPayMethodId(id)
  // juste avant serait encore en attente (state asynchrone) au moment de
  // l'appel API si on relisait payMethodId ici.
  const addPayment = async (methodId = payMethodId) => {
    const parsedAmount = parseFloat(payAmount);
    if (!payAmount || isNaN(parsedAmount) || parsedAmount === 0) return toast.error('Montant invalide');
    if (!methodId) return toast.error('Moyen de paiement requis');
    if (paySubmitting) return; // évite un double-clic double-enregistrement
    setPaySubmitting(true);
    setPayMethodId(methodId);
    try {
      await paymentsApi.add({ bookingId: id, paymentMethodId: methodId, amount: parsedAmount });
      toast.success('Paiement enregistré');
      setPayAmount('');
      setPayMethodId('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur');
    } finally {
      setPaySubmitting(false);
    }
  };

  // Trop perçu : crée une demande de remboursement (workflow existant,
  // validée par un manager) pré-remplie avec le montant excédentaire —
  // plutôt que de laisser le total encaissé dépasser le total dû sans
  // action possible depuis la fiche résa.
  const requestOverpaymentRefund = async (overAmount) => {
    if (refundSubmitting || pendingRefund) return;
    setRefundSubmitting(true);
    try {
      const r = await refundRequestsApi.create({
        bookingId: id,
        amount: overAmount,
        reason: `Trop perçu sur la réservation ${booking.booking_ref}`,
      });
      setPendingRefund(r.data.refundRequest);
      toast.success('Demande de remboursement créée — en attente de validation');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setRefundSubmitting(false);
    }
  };

  const assignGM = async gmId => {
    try { await bookingsApi.update(id, { gmUserId: gmId }); toast.success('GM assigné'); load(); }
    catch { toast.error('Erreur'); }
  };

  const changeStatus = async status => {
    try { await bookingsApi.update(id, { status }); toast.success('Statut mis à jour'); load(); }
    catch { toast.error('Erreur'); }
  };

  const sendReminder = async () => {
    setRemindModal(true);
  };

  if (loading) return <div className="loading-screen"><span className="spinner"/></div>;
  if (!booking) return (
    <div className="page-body">
      <div className="empty-state">
        <p>Réservation introuvable</p>
        <button className="btn btn-secondary" onClick={() => navigate('/app/reservations')}>Retour</button>
      </div>
    </div>
  );

  // Arrondi à 2 décimales sur chaque étape : une simple soustraction de
  // flottants JS (96 - 30.4 - 30.4 - ...) peut laisser un résidu du type
  // -0.00000000004, ce qui ferait basculer à tort l'affichage entre "Soldé"
  // et "Trop perçu"/"Reste dû" pour rien.
  const amountPaid = Math.round((booking.payments || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0) * 100) / 100;
  const remaining  = Math.round((parseFloat(booking.total_amount) - amountPaid) * 100) / 100;
  // Le nombre de joueurs/total restent modifiables même après paiement (appel
  // client fréquent) — seule la remise se verrouille, et seulement après un
  // paiement EN CAISSE (POS) réel, pas un acompte en ligne.
  const hasPosPayment = (booking.payments || []).some(p => p.isPos && parseFloat(p.amount || 0) > 0);

  // price_breakdown : uniquement depuis la base
  const priceBreakdown = (() => {
    if (!booking.price_breakdown) return null;
    const stored = Array.isArray(booking.price_breakdown)
      ? booking.price_breakdown
      : (typeof booking.price_breakdown === 'string'
          ? (() => { try { return JSON.parse(booking.price_breakdown); } catch { return null; } })()
          : null);
    return stored && stored.length > 0 ? stored : null;
  })();

  const ef = k => ({ value: editForm[k] || '', onChange: e => setEditForm(f => ({ ...f, [k]: e.target.value })) });

  return (
    <>
      {/* ── En-tête ── */}
      <div className="page-header">
        <div>
          <button className="btn btn-ghost btn-sm" style={{ marginBottom: 8 }} onClick={() => navigate('/app/reservations')}>
            <ArrowLeft size={15}/> Retour aux réservations
          </button>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
            <h2 style={{ margin:0, fontSize:20 }}>{booking.booking_ref}</h2>
            <span className={`badge ${STATUS_BADGE[booking.status] || 'badge-gray'}`}>
              {STATUS_LABELS[booking.status] || booking.status}
            </span>
            <span className={`badge ${PAY_BADGE[booking.payment_status] || 'badge-gray'}`}>
              {PAY_LABELS[booking.payment_status] || booking.payment_status}
            </span>
          </div>
          <p style={{ color:'var(--text-2)', margin:0 }}>
            {booking.room_name} · {format(new Date(booking.session_date), "EEEE d MMMM yyyy", { locale:fr })} à {booking.session_start?.slice(0,5)}
          </p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {can('bookings.edit') && (
            <button className="btn btn-secondary btn-sm" onClick={() => setMoveModal(true)}>
              Déplacer
            </button>
          )}
          {can('manager') && (
            <button className="btn btn-secondary btn-sm" onClick={sendReminder}>
              <Bell size={14}/> Envoyer un rappel
            </button>
          )}
          {booking.status !== 'cancelled' && can('bookings.cancel') && (
            <button className="btn btn-danger btn-sm" onClick={() => setCancelModal(true)}>
              <X size={13}/> Annuler la réservation
            </button>
          )}
        </div>
      </div>

      {booking.status === 'pending' && (
        <div style={{ margin:'0 0 16px', padding:'10px 16px', borderRadius:10,
          background:'rgba(245,166,35,0.1)', border:'1px solid rgba(245,166,35,0.3)',
          display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
          <span style={{ color:'var(--amber)', flexShrink:0 }}>⏳</span>
          <strong style={{ color:'var(--amber)', flexShrink:0 }}>En attente de paiement</strong>
          {booking.expires_at && (
            <span style={{ color:'var(--text-3)', fontSize:12, marginLeft:'auto' }}>
              Expire à {new Date(booking.expires_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
            </span>
          )}
        </div>
      )}
      <div className="page-body">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(0, 1fr))', gap:20 }}>

          {/* ── Client ── */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Informations client</span>
              {can('bookings.edit') && !editingContact && (
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingContact(true)}>
                  <Edit2 size={14}/> Modifier
                </button>
              )}
              {editingContact && (
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveContact}><Save size={13}/> Enregistrer</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingContact(false)}><X size={13}/></button>
                </div>
              )}
            </div>

            {/* ── Infos client en lecture ── */}
            {!editingContact ? (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {booking.customer_type === 'company' && (
                  <div style={{ marginBottom:8, padding:'8px 12px', borderRadius:8,
                    background:'rgba(124,92,252,0.08)', border:'1px solid rgba(124,92,252,0.2)' }}>
                    <div style={{ fontSize:11, color:'var(--accent-light)', fontWeight:700, marginBottom:6 }}>
                      🏢 Réservation entreprise
                    </div>
                    {booking.company_name    && <div style={{ fontWeight:700, fontSize:14 }}>{booking.company_name}</div>}
                    {booking.company_siret   && <div style={{ fontSize:12, color:'var(--text-3)' }}>SIRET : {booking.company_siret}</div>}
                    {booking.company_vat     && <div style={{ fontSize:12, color:'var(--text-3)' }}>TVA : {booking.company_vat}</div>}
                    {booking.company_address && <div style={{ fontSize:12, color:'var(--text-3)' }}>{booking.company_address}</div>}
                  </div>
                )}
              {[
                  ['Contact',   booking.customer_name],
                  ['Email',     booking.customer_email],
                  ['Téléphone', booking.customer_phone || '—'],
                  ['Source',    {'online':'En ligne','admin':'Admin','phone':'Téléphone','walk-in':'Sur place','gift':'Bon cadeau'}[booking.source] || booking.source],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div className="text-xs text-muted" style={{ marginBottom:2 }}>{label}</div>
                    <div style={{ fontWeight:500 }}>{val}</div>
                  </div>
                ))}
                {booking.internal_notes && (
                  <div style={{ background:'var(--bg-elevated)', borderRadius:8, padding:'10px 12px' }}>
                    <div className="text-xs text-muted" style={{ marginBottom:4 }}>Notes internes</div>
                    <p style={{ fontSize:14, margin:0 }}>{booking.internal_notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nom</label>
                    <input className="input" {...ef('customerName')}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="input" {...ef('customerEmail')}/>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Téléphone</label>
                  <input type="tel" className="input" value={editForm.customerPhone||''}
                    onChange={e=>setEditForm(f=>({...f,customerPhone:formatPhone(e.target.value)}))}/>
                </div>
                {/* Toggle type client */}
                <div className="form-group">
                  <label className="form-label">Type de client</label>
                  <div style={{ display:'flex', gap:8 }}>
                    {[['individual','👤 Particulier'],['company','🏢 Entreprise']].map(([val, label]) => (
                      <button key={val} type="button"
                        onClick={() => setEditForm(f => ({ ...f, customerType: val }))}
                        style={{ flex:1, padding:'8px', borderRadius:8, cursor:'pointer',
                          fontFamily:'inherit', fontSize:12, fontWeight:600,
                          border:`1.5px solid ${editForm.customerType===val ? 'var(--accent)' : 'var(--border)'}`,
                          background: editForm.customerType===val ? 'rgba(124,92,252,0.1)' : 'var(--bg-elevated)',
                          color: editForm.customerType===val ? 'var(--accent-light)' : 'var(--text-2)' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {editForm.customerType === 'company' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Raison sociale</label>
                      <input className="input" {...ef('companyName')} placeholder="SARL Mon Entreprise"/>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">SIRET</label>
                        <input className="input" {...ef('companySiret')} maxLength={14}/>
                      </div>
                      <div className="form-group">
                        <label className="form-label">N° TVA</label>
                        <input className="input" {...ef('companyVat')}/>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Adresse de facturation</label>
                      <input className="input" {...ef('companyAddress')}/>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label className="form-label">Notes internes</label>
                  <textarea className="input" rows={3} style={{ resize:'vertical' }} {...ef('internalNotes')}/>
                </div>
              </div>
            )}
          </div>

          {/* ── Fidélité ── */}
          <LoyaltyPlayersCard bookingId={id} booking={booking} />

          {/* ── Composition du groupe ── */}
          <div className="card">
            <div className="card-header">
              <span className="card-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
                Composition du groupe
                {appliedType && (
                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:99,
                  background: appliedType==='hp' ? 'rgba(245,100,50,0.15)' : 'rgba(0,210,180,0.15)',
                  color: appliedType==='hp' ? '#f56432' : 'var(--teal)' }}>
                  Tarif {appliedType.toUpperCase()}
                </span>
              )}
              </span>
              {can('bookings.edit') && !editingPlayers && (
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingPlayers(true)}>
                  <Edit2 size={14}/> Modifier
                </button>
              )}
              {editingPlayers && (
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-primary btn-sm" onClick={savePlayers}><Save size={13}/> Enregistrer</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingPlayers(false)}><X size={13}/></button>
                </div>
              )}
            </div>

            {!editingPlayers ? (
              <div>
                {/* Détail par profil si disponible */}
                {priceBreakdown && priceBreakdown.length > 0 ? (
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    {priceBreakdown.map((b, i) => (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between',
                        alignItems:'center', padding:'8px 12px',
                        background:'var(--bg-elevated)', borderRadius:8 }}>
                        <div>
                          <span style={{ fontWeight:600, fontSize:14 }}>
                            {b.qty > 1 ? `${b.qty}× ` : ''}{b.profileName}
                          </span>
                        </div>
                        <span style={{ fontWeight:700, color:'var(--teal)', fontSize:14 }}>
                          {parseFloat(b.lineTotal).toFixed(2)} €
                        </span>
                      </div>
                    ))}
                    <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700,
                      padding:'10px 12px', borderTop:'1px solid var(--border)', marginTop:4,
                      color:'var(--text-2)', fontSize:13 }}>
                      <span>{priceBreakdown.reduce((s,b)=>s+(b.qty||0),0)} joueur{priceBreakdown.reduce((s,b)=>s+(b.qty||0),0)>1?'s':''}</span>
                      <span style={{ color:'var(--teal)', fontWeight:700, fontSize:15 }}>{parseFloat(booking.total_amount).toFixed(2)} €</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    padding:'12px 14px', background:'var(--bg-elevated)', borderRadius:8 }}>
                    <span style={{ fontSize:14 }}>{booking.player_count} joueur{booking.player_count>1?'s':''}</span>
                    <span style={{ fontWeight:700, color:'var(--teal)' }}>
                      {parseFloat(booking.total_amount).toFixed(2)} €
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {roomPrices.length > 0 ? (
                  <AdminPlayerPicker prices={roomPrices} appliedType={appliedType} initialPlayerCount={editForm.playerCount||1}
                    initialBreakdown={editForm.priceBreakdown}
                    onChange={p=>{setEditForm(f=>({...f,playerCount:p.totalPlayers,totalAmount:p.total,priceBreakdown:p.breakdown}));}}/>
                ) : (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Nb joueurs</label>
                      <input type="number" className="input" {...ef('playerCount')}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Montant (€)</label>
                      <input type="number" step="0.01" className="input" {...ef('totalAmount')}/>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Assignation GM ── */}
          <div className="card">
            <div className="card-title" style={{ marginBottom:14 }}>
              <UserCheck size={15} style={{ display:'inline', marginRight:6 }}/>
              Game Master
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:300, overflowY:'auto' }}>
              {staffList
                .filter(s => s.is_available || booking.gm_user_id === s.id) // backend filtre absents
                .map(s => {
                const isAssigned = booking.gm_user_id === s.id;
                const isFull = s.assigned_count >= 2;
                const disabled = isFull && !isAssigned;
                return (
                  <div key={s.id}
                    onClick={() => !disabled && assignGM(isAssigned ? null : s.id)}
                    style={{
                      display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
                      borderRadius:8, cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      background: isAssigned ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                      border: `1px solid ${isAssigned ? 'var(--accent)' : 'var(--border)'}`,
                      transition:'all 0.12s',
                    }}>
                    <div className="avatar" style={{ width:28, height:28, fontSize:11 }}>
                      {s.first_name?.[0]}{s.last_name?.[0]}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13 }}>{s.first_name} {s.last_name}</div>
                      <div style={{ display:'flex', gap:4, marginTop:2, flexWrap:'wrap' }}>
                        <span className="text-xs text-muted">{s.role}</span>
                        {s.assigned_count > 0 && (
                          <span className={`badge ${s.assigned_count >= 2 ? 'badge-red' : 'badge-amber'}`}
                            style={{ fontSize:9 }}>
                            {s.assigned_count}/2 salle{s.assigned_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    {isFull && !isAssigned ? (
                      <span className="badge badge-red">Complet 2/2</span>
                    ) : isAssigned ? (
                      <span className="badge badge-green">Assigné</span>
                    ) : s.assigned_count > 0 ? (
                      <span className="badge badge-amber">{s.assigned_count}/2 salle{s.assigned_count>1?'s':''}</span>
                    ) : (
                      <span className="badge badge-green">Dispo</span>
                    )}
                  </div>
                );
              })}
              {staffList.length === 0 && (
                <p className="text-sm text-muted">
                  Aucun GM qualifié disponible ce jour pour cette salle
                </p>
              )}
            </div>
          </div>

          {/* ── Paiements ── */}
          {can('payments.view') && <div className="card" style={{ gridColumn:'1/-1' }}>
            <div className="card-title" style={{ marginBottom:16 }}>
              <CreditCard size={15} style={{ display:'inline', marginRight:6 }}/>
              Paiements
            </div>
            <div>

              {/* Historique */}
              <div>
                {(booking.payments || []).length === 0 ? (
                  <p className="text-sm text-muted">Aucun paiement enregistré</p>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Date / heure</th>
                          <th>Moyen de paiement</th>
                          <th className="text-right">Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(booking.payments || []).map(p => (
                          <tr key={p.id}>
                            <td className="text-sm text-muted">
                              {p.paidAt ? format(new Date(p.paidAt), 'dd/MM/yyyy HH:mm') : '—'}
                            </td>
                            <td>{p.method || (parseFloat(p.amount) < 0 ? 'Remboursement' : '—')}</td>
                            <td className="text-right bold" style={{ color: parseFloat(p.amount) < 0 ? 'var(--red)' : 'var(--teal)' }}>
                              {parseFloat(p.amount) < 0
                                ? `${parseFloat(p.amount).toFixed(2)} €`
                                : `+${parseFloat(p.amount).toFixed(2)} €`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Remise manuelle / partenaire — intégrée ici (pas de carte
                    dédiée) pour ne pas surcharger la fiche de blocs */}
                <DiscountCard bookingId={id} booking={booking} canEdit={can('bookings.edit')} onChanged={load} locked={hasPosPayment} />

                {/* Récap financier — Reste dû distingue 3 cas : reste à payer
                    (amber), soldé pile (vert), et trop perçu (rouge, remaining
                    strictement négatif) plutôt que d'afficher "Soldé" pour un
                    encaissement supérieur au total, ce qui masquerait l'erreur. */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginTop:16 }}>
                  {[
                    ['Total attendu', `${parseFloat(booking.total_amount).toFixed(2)} €`, 'var(--text-1)'],
                    ['Encaissé',      `${amountPaid.toFixed(2)} €`,                       'var(--green)'],
                    ['Reste dû',
                      remaining < 0 ? `Trop perçu : ${Math.abs(remaining).toFixed(2)} €`
                        : remaining === 0 ? '✓ Soldé'
                        : `${remaining.toFixed(2)} €`,
                      remaining < 0 ? 'var(--red)' : remaining === 0 ? 'var(--green)' : 'var(--amber)'],
                  ].map(([label, val, color]) => (
                    <div key={label} style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 16px' }}>
                      <div className="text-xs text-muted" style={{ marginBottom:4 }}>{label}</div>
                      <div style={{ fontWeight:700, fontSize:18, color }}>{val}</div>
                    </div>
                  ))}
                </div>
                {remaining < 0 && (
                  <div style={{ marginTop:8, padding:'10px 14px', borderRadius:8,
                    background:'rgba(233,69,96,0.08)', border:'1px solid var(--red)',
                    fontSize:12, color:'var(--red)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom: can('payments.create') ? 8 : 0 }}>
                      ⚠️ Le montant encaissé dépasse le total de la réservation de {Math.abs(remaining).toFixed(2)} € —
                      vérifie les paiements enregistrés ci-dessus (erreur de saisie ou remboursement à prévoir).
                    </div>
                    {can('payments.create') && (
                      pendingRefund ? (
                        <span style={{ fontSize:12, color:'var(--amber)' }}>
                          ⏳ Demande de remboursement de {parseFloat(pendingRefund.amount).toFixed(2)} € en attente de validation
                        </span>
                      ) : (
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => requestOverpaymentRefund(Math.abs(remaining))}
                          disabled={refundSubmitting}>
                          {refundSubmitting ? <span className="spinner" style={{width:14,height:14}}/> : `Créer une demande de remboursement (${Math.abs(remaining).toFixed(2)} €)`}
                        </button>
                      )
                    )}
                  </div>
                )}
                {/* Détail TVA */}
                {tva.rate > 0 && (
                  <div style={{ marginTop:8, padding:'8px 14px', borderRadius:8,
                    background:'var(--bg-elevated)', fontSize:12, color:'var(--text-3)',
                    display:'flex', gap:16, flexWrap:'wrap' }}>
                    {tva.mode==='ht' ? (
                      <>
                        <span>HT : {parseFloat(booking.total_amount||0).toFixed(2)} €</span>
                        <span>TVA {tva.rate}% : {(parseFloat(booking.total_amount||0)*tva.rate/100).toFixed(2)} €</span>
                        <span style={{color:'var(--text-2)',fontWeight:600}}>
                          TTC : {(parseFloat(booking.total_amount||0)*(1+tva.rate/100)).toFixed(2)} €
                        </span>
                      </>
                    ) : (
                      <>
                        <span>HT : {(parseFloat(booking.total_amount||0)/(1+tva.rate/100)).toFixed(2)} €</span>
                        <span>TVA {tva.rate}% : {(parseFloat(booking.total_amount||0)-parseFloat(booking.total_amount||0)/(1+tva.rate/100)).toFixed(2)} €</span>
                        <span style={{color:'var(--text-2)',fontWeight:600}}>
                          TTC : {parseFloat(booking.total_amount||0).toFixed(2)} €
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Reçu */}
              {can('payments.view') && (
                <div style={{ display:'flex', gap:8, margin:'16px 0' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleReceipt('download')} disabled={genReceipt}>
                    <FileText size={13}/> {genReceipt ? '...' : 'Télécharger le reçu'}
                  </button>
                  {booking.customer_email && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleReceipt('send')} disabled={genReceipt}>
                      <Mail size={13}/> Envoyer le reçu
                    </button>
                  )}
                </div>
              )}

              {/* Bon cadeau + Encaissement, côte à côte, en pleine largeur
                  (plus la colonne étroite de 300px, moins pratique) */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, alignItems:'start' }}>

                {/* Bon cadeau */}
                {can('payments.create') && (
                  <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:18 }}>
                    <div style={{ fontWeight:600, fontSize:14, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
                      <Gift size={15} color="var(--accent-light)"/> Bon cadeau
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <input className="input" style={{ flex:1 }} value={giftCode}
                        onChange={e => { setGiftCode(e.target.value.toUpperCase()); setGiftCard(null); }}
                        placeholder="EG-XXXXXXXX"
                        onKeyDown={e => { if (e.key==='Enter') { e.stopPropagation(); checkGiftCard(); } }}/>
                      <button className="btn btn-secondary" onClick={checkGiftCard} disabled={checkingGift}>
                        {checkingGift ? <span className="spinner" style={{width:14,height:14}}/> : 'Vérifier'}
                      </button>
                    </div>
                    {giftCard && (
                      <div style={{ marginTop:10, padding:'10px 12px', background:'rgba(0,210,180,0.08)',
                        border:'1px solid var(--teal)', borderRadius:8 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600, color:'var(--teal)' }}>
                              ✓ Bon valide — {parseFloat(giftCard.remaining_amount).toFixed(2)} € disponible
                            </div>
                            {giftCard.purchased_by_name && (
                              <div style={{ fontSize:11, color:'var(--text-3)' }}>Pour : {giftCard.purchased_by_name}</div>
                            )}
                          </div>
                          <button className="btn btn-teal btn-sm" onClick={applyGiftCard}>
                            Appliquer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Formulaire encaissement */}
                <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:18 }}>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:14 }}>Encaisser un paiement</div>
                  <div className="form-group">
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                      <label className="form-label">Montant (€)</label>
                      {payAmount && (
                        <button type="button" onClick={() => setPayAmount('')}
                          style={{ background:'none', border:'none', color:'var(--text-3)', fontSize:11, cursor:'pointer' }}>
                          Effacer
                        </button>
                      )}
                    </div>
                    {/* Vrai <input> focusable : sur ordinateur, les touches du
                        clavier physique (0-9, Retour arrière) déclenchent
                        exactement la même logique de décalage "caisse" que le
                        pavé tactile ci-dessous (via applyNumpadKey) — pas de
                        saisie libre, pas de virgule à gérer. Les deux modes
                        écrivent dans le même état payAmount. */}
                    <div style={{
                      display:'flex', alignItems:'center', gap:6,
                      padding:'10px 14px', borderRadius:10, marginBottom:10,
                      background:'var(--bg-base)', border:'1px solid var(--border)',
                    }}>
                      <input
                        type="text" inputMode="numeric" autoComplete="off" readOnly
                        value={payAmount ? payAmount.replace('.', ',') : ''}
                        onKeyDown={e => {
                          const k = e.key === 'Delete' ? '⌫' : e.key;
                          if (/^[0-9]$/.test(k) || k === '⌫') {
                            e.preventDefault();
                            setPayAmount(applyNumpadKey(payAmount, k));
                          }
                        }}
                        placeholder={remaining > 0 ? `Reste : ${remaining.toFixed(2)}` : '0,00'}
                        style={{
                          flex:1, minWidth:0, textAlign:'right', background:'none', border:'none', outline:'none',
                          fontSize:24, fontWeight:700, color:'var(--text-1)', fontFamily:'inherit', padding:0, cursor:'text',
                        }}
                      />
                      <span style={{ fontSize:24, fontWeight:700, color: payAmount ? 'var(--text-1)' : 'var(--text-3)' }}>€</span>
                    </div>
                    <NumPad value={payAmount} onChange={setPayAmount} />
                  </div>
                  {remaining > 0 && (
                    <>
                      <div style={{ display:'flex', gap:6, marginBottom:8, flexWrap:'wrap' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setPayAmount(remaining.toFixed(2))}>
                          Solde ({remaining.toFixed(2)} €)
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setPayAmount((remaining * 0.5).toFixed(2))}>
                          50%
                        </button>
                      </div>
                      <div style={{ marginBottom:12 }}>
                        <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                          <button type="button"
                            className={`btn btn-sm ${splitMode === 'equal' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setSplitMode('equal')}>
                            Parts égales
                          </button>
                          {priceBreakdown && (
                            <button type="button"
                              className={`btn btn-sm ${splitMode === 'tariff' ? 'btn-primary' : 'btn-secondary'}`}
                              onClick={() => setSplitMode('tariff')}>
                              Par tarif
                            </button>
                          )}
                        </div>

                        {splitMode === 'equal' ? (
                          <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                            <span className="text-xs text-muted" style={{ whiteSpace:'nowrap' }}>Diviser en</span>
                            <input type="number" min="2" step="1" className="input" style={{ width:56, padding:'4px 8px' }}
                              value={splitParts}
                              onChange={e => {
                                const v = e.target.value;
                                setSplitParts(v);
                                const n = parseInt(v, 10);
                                if (n >= 2) setPayAmount((remaining / n).toFixed(2));
                              }} placeholder="X" />
                            <span className="text-xs text-muted" style={{ whiteSpace:'nowrap' }}>
                              parts{parseInt(splitParts, 10) >= 2 ? ` → ${(remaining / parseInt(splitParts, 10)).toFixed(2)} € / part` : ''}
                            </span>
                          </div>
                        ) : (
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            {priceBreakdown.map((line, i) => {
                              const lineTotal = parseFloat(line.lineTotal) || 0;
                              const perUnit = line.qty > 1 ? lineTotal / line.qty : lineTotal;
                              return (
                                <button type="button" key={line.profileId || i} className="btn btn-secondary btn-sm"
                                  onClick={() => setPayAmount(Math.min(perUnit, remaining).toFixed(2))}
                                  title={line.qty > 1 ? `${lineTotal.toFixed(2)} € pour les ${line.qty}` : undefined}>
                                  {line.profileName} — {perUnit.toFixed(2)} €
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label className="form-label">
                      Moyen de paiement — clique pour encaisser {payAmount ? `${parseFloat(payAmount || 0).toFixed(2)} €` : ''}
                    </label>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(80px, 1fr))', gap:8 }}>
                      {methods.map(m => {
                        const ICON_MAP = {
                          'cash':Banknote,'banknotes':Banknote,
                          'credit-card':CardIcon,'card':CardIcon,'stripe':CardIcon,
                          'cheque':ScrollText,'document':ScrollText,
                          'transfer':Landmark,'arrow-right':Landmark,
                          'gift':Gift,'cb_distance':Phone,
                        };
                        const Icon = ICON_MAP[m.icon] || CardIcon;
                        const submittingThis = paySubmitting && payMethodId === m.id;
                        const noAmount = !payAmount || isNaN(parseFloat(payAmount)) || parseFloat(payAmount) === 0;
                        return (
                          <button type="button" key={m.id} onClick={() => addPayment(m.id)}
                            title={m.name} disabled={paySubmitting || noAmount}
                            style={{
                              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4,
                              height:52, padding:'6px 6px', borderRadius:10,
                              cursor: (paySubmitting || noAmount) ? 'not-allowed' : 'pointer', overflow:'hidden',
                              opacity: noAmount && !paySubmitting ? 0.5 : 1,
                              background: 'var(--bg-base)',
                              border:'1px solid var(--border)',
                              transition:'all 0.12s',
                            }}>
                            {submittingThis ? (
                              <span className="spinner" style={{ width:16, height:16 }} />
                            ) : (
                              <Icon size={16} color="var(--text-2)" />
                            )}
                            <span style={{ fontSize:10, color:'var(--text-3)',
                              textAlign:'center', lineHeight:1.2, whiteSpace:'nowrap',
                              overflow:'hidden', textOverflow:'ellipsis', maxWidth:'100%' }}>{m.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        </div>
      </div>


      {/* Modale annulation */}
      {cancelModal && (() => {
        const sessionTime = booking.session_start?.slice(0,5) || '00:00';
        const sessionDt   = new Date(booking.session_date.slice(0,10) + 'T' + sessionTime + ':00');
        const hoursUntil  = (sessionDt - Date.now()) / 3600000;
        const canRefund   = hoursUntil > 48;
        const amountPaid  = Math.round((booking.payments || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0) * 100) / 100;

        const doCancel = async (action) => {
          try {
            await changeStatus('cancelled');
            if (action === 'refund') {
              // Créer une demande de remboursement en attente de validation
              await refundRequestsApi.create({
                bookingId: booking.id,
                amount: amountPaid,
                reason: `Annulation réservation ${booking.booking_ref} — session > 48h`,
              });
              toast.success('Réservation annulée — demande de remboursement créée, en attente de validation');
            } else if (action === 'avoir') {
              // Générer un avoir lié au client
              await giftCardsApi.create({
                amount: amountPaid,
                giftType: 'avoir',
                sourceBookingId: booking.id,
                customerId: booking.customer_id || null,
                purchasedByName: booking.customer_name,
                purchasedByEmail: booking.customer_email || null,
                message: `Avoir suite à annulation réservation ${booking.booking_ref}`,
              });
              toast.success(`Réservation annulée — avoir de ${amountPaid.toFixed(2)} € généré pour ${booking.customer_name}`);
            } else if (action === 'simple') {
              // Passer sendCancelNotif pour déclencher l'email côté backend
              await bookingsApi.update(id, { status: 'cancelled', sendCancelNotif: true });
              toast.success('Réservation annulée');
              // Eviter le double changeStatus plus bas
              setCancelModal(false); load(); return;
            } else {
              toast.success('Réservation annulée');
            }
          } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
          setCancelModal(false);
          load();
        };

        return (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCancelModal(false)}>
            <div className="modal" style={{ maxWidth: 460 }}>
              <h3 className="modal-title">Annuler la réservation</h3>
              <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
                Réservation <strong>{booking.booking_ref}</strong> — {booking.room_name}<br/>
                {amountPaid > 0 && <span>Montant encaissé : <strong>{amountPaid.toFixed(2)} €</strong></span>}
              </p>

              {/* Bandeau info selon délai */}
              <div style={{ padding: '12px 14px', borderRadius: 10, marginBottom: 16, fontSize: 13,
                background: canRefund ? 'rgba(0,210,180,0.06)' : 'rgba(245,166,35,0.06)',
                border: `1px solid ${canRefund ? 'var(--teal)' : 'var(--amber)'}` }}>
                <div style={{ fontWeight: 600, color: canRefund ? 'var(--teal)' : 'var(--amber)', marginBottom: 4 }}>
                  {canRefund ? '✓ Session dans plus de 48h' : '⚠ Session dans moins de 48h'}
                </div>
                <div style={{ color: 'var(--text-2)' }}>
                  {canRefund
                    ? 'Un remboursement peut être demandé. Il sera validé par un manager.'
                    : amountPaid > 0
                      ? 'Le délai de remboursement est dépassé. Un avoir peut être généré.'
                      : 'Aucun paiement enregistré sur cette réservation.'}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* CAS 1 : paiement + > 48h → demande de remboursement */}
                {amountPaid > 0 && canRefund && (
                  <button className="btn btn-teal" style={{ justifyContent: 'center' }}
                    onClick={() => doCancel('refund')}>
                    ↩ Annuler + demander le remboursement ({amountPaid.toFixed(2)} €)
                  </button>
                )}

                {/* CAS 2 : paiement + < 48h → avoir client */}
                {amountPaid > 0 && !canRefund && (
                  <button className="btn btn-secondary" style={{ justifyContent: 'center' }}
                    onClick={() => doCancel('avoir')}>
                    🎁 Annuler + générer un avoir ({amountPaid.toFixed(2)} €)
                  </button>
                )}

                {/* CAS 3 : aucun paiement → annulation simple */}
                {amountPaid === 0 && (
                  <button className="btn btn-danger" style={{ justifyContent: 'center' }}
                    onClick={() => doCancel('simple')}>
                    <X size={13}/> Annuler la réservation
                  </button>
                )}

                <button className="btn btn-ghost btn-sm" style={{ justifyContent: 'center' }}
                  onClick={() => setCancelModal(false)}>
                  Conserver la réservation
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bloc notifications de cette résa ── */}
      {bookingNotifs.length > 0 && (
        <div className="card" style={{ marginTop:0 }}>
          <div className="card-title" style={{ marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
            <Bell size={14}/> Notifications envoyées
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {bookingNotifs.map(n => (
              <div key={n.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px',
                borderRadius:8, background:'var(--bg-elevated)', fontSize:12 }}>
                <span style={{ flexShrink:0 }}>
                  {n.status === 'sent'   ? <CheckCircle2 size={13} color="var(--teal)"/> :
                   n.status === 'failed' ? <X size={13} color="var(--red)"/> :
                   <Clock size={13} color="var(--amber)"/>}
                </span>
                <span style={{ color:'var(--text-3)', fontFamily:'monospace', flexShrink:0, fontSize:11 }}>
                  {n.created_at ? new Date(n.created_at).toLocaleString('fr-FR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : '—'}
                </span>
                <span style={{ flex:1, color:'var(--text-2)' }}>{n.subject}</span>
                <span style={{ fontSize:11, color:'var(--text-3)', flexShrink:0 }}>
                  {n.triggered_by_first ? `${n.triggered_by_first} ${n.triggered_by_last}` : 'Auto'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Modale rappel ── */}
      {remindModal && (
        <ReminderModal
          bookingId={id}
          onClose={() => setRemindModal(false)}
          onSent={() => {
            setRemindModal(false);
            notifsApi.list({ bookingId: id }).then(r => setBookingNotifs(r.data.notifications || []));
            toast.success('Rappel envoyé');
          }}
        />
      )}

      {moveModal && (
        <MoveBookingModal
          booking={booking}
          onClose={() => setMoveModal(false)}
          onMoved={() => { setMoveModal(false); load(); }}
        />
      )}
    </>
  );
}
